import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';

// Page imports
import JobManagement from './pages/corporate/job-management';
import ContractorJobExecution from './pages/contractor/job-execution';
import WorkforceManagement from './pages/corporate/workforce-management';
import AuditLoggingSystem from './pages/common/audit-logging-system';
import CompanyManagementPortal from './pages/super-admin/company-management';
import MultiCompanySwitcher from './pages/common/multi-company-switcher';
import CompanyMergeSimulation from './pages/super-admin/company-merge-simulation';
import PerformanceHealthMonitor from './pages/super-admin/performance-health-monitor';
import GlobalLoadingStates from './pages/super-admin/global-loading-states';
import ErrorBoundarySystem from './pages/super-admin/error-boundary-system';
import AcceptInvitation from './pages/common/accept-invitation';

import TodayJobs from './pages/contractor/today-jobs';
import UpcomingJobs from './pages/contractor/upcoming-jobs';
import Earnings from './pages/contractor/earnings';
import AdvancedAnalytics from './pages/contractor/earnings/AdvancedAnalytics';
import SettingsPage from './pages/common/settings';
import CorporateSettings from './pages/corporate/settings';
import ContractorSettings from './pages/contractor/settings';
import CorporateReports from './pages/corporate/reports';
import CorporateEarnings from './pages/corporate/earnings';
import ClientsAgencies from './pages/corporate/clients-agencies';

// Lazy load pages for code splitting
const Landing = lazy(() => import('./pages/common/landing'));
const ContractorLogin = lazy(() => import('./pages/contractor/login'));
const CorporateLogin = lazy(() => import('./pages/corporate/login'));
const SuperAdminLogin = lazy(() => import('./pages/super-admin/login'));
const ContractorDashboard = lazy(() => import('./pages/contractor/dashboard'));
const CorporateDashboard = lazy(() => import('./pages/corporate/dashboard'));
const SuperAdminDashboard = lazy(() => import('./pages/super-admin/dashboard'));

// Quotations module - lazy loaded
const CorporateQuotes = lazy(() => import('./pages/corporate/quotes'));
const QuoteEditor = lazy(() => import('./pages/corporate/quotes/QuoteEditor'));
const ViewQuote = lazy(() => import('./pages/corporate/quotes/ViewQuote'));

// Super Admin pages
const AllUsers = lazy(() => import('./pages/super-admin/all-users'));

// Inspection Templates & Inspections module - lazy loaded
const CorporateTemplates = lazy(() => import('./pages/corporate/templates'));
const TemplateBuilder = lazy(() => import('./pages/corporate/templates/builder'));
const CorporateInspections = lazy(() => import('./pages/corporate/inspections'));
const ContractorInspections = lazy(() => import('./pages/contractor/inspections'));
const ConductInspection = lazy(() => import('./pages/common/inspection/conduct'));
const ReviewInspection = lazy(() => import('./pages/common/inspection/review'));
const ViewInspection = lazy(() => import('./pages/common/inspection/view'));
const InspectionPdf = lazy(() => import('./pages/common/inspection/pdf'));
const InspectionReport = lazy(() => import('./pages/common/inspection/report'));

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-900">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      <p className="mt-4 text-gray-400">Loading...</p>
    </div>
  </div>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<LoadingFallback />}>
          <RouterRoutes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/contractor-login" element={<ContractorLogin />} />
            <Route path="/corporate-login" element={<CorporateLogin />} />
            <Route path="/super-admin-login" element={<SuperAdminLogin />} />
            <Route path="/accept-invite" element={<AcceptInvitation />} />

            {/* Corporate Portal - Protected */}
            <Route path="/corporate/dashboard" element={<ProtectedRoute allowedRoles={['corporate']}><CorporateDashboard /></ProtectedRoute>} />
            <Route path="/corporate/settings" element={<ProtectedRoute allowedRoles={['corporate']}><CorporateSettings /></ProtectedRoute>} />

            {/* Contractor Portal - Protected */}
            <Route path="/contractor/dashboard" element={<ProtectedRoute allowedRoles={['contractor']}><ContractorDashboard /></ProtectedRoute>} />
            <Route path="/contractor/earnings-tracker" element={<ProtectedRoute allowedRoles={['contractor']}><AdvancedAnalytics /></ProtectedRoute>} />
            <Route path="/contractor-job-execution" element={<ProtectedRoute allowedRoles={['contractor']}><ContractorJobExecution /></ProtectedRoute>} />
            <Route path="/contractor/today-jobs" element={<ProtectedRoute allowedRoles={['contractor']}><TodayJobs /></ProtectedRoute>} />
            <Route path="/contractor/upcoming-jobs" element={<ProtectedRoute allowedRoles={['contractor']}><UpcomingJobs /></ProtectedRoute>} />
            <Route path="/contractor/earnings" element={<ProtectedRoute allowedRoles={['contractor']}><Earnings /></ProtectedRoute>} />
            <Route path="/contractor/settings" element={<ProtectedRoute allowedRoles={['contractor']}><ContractorSettings /></ProtectedRoute>} />

            {/* Super Admin Portal - Protected */}
            <Route path="/super-admin" element={<SuperAdminLogin />} />
            <Route path="/super-admin/dashboard" element={<ProtectedRoute allowedRoles={['superadmin']}><SuperAdminDashboard /></ProtectedRoute>} />
            <Route path="/super-admin/settings" element={<ProtectedRoute allowedRoles={['superadmin']}><SettingsPage /></ProtectedRoute>} />
            <Route path="/super-admin/companies" element={<ProtectedRoute allowedRoles={['superadmin']}><CompanyManagementPortal /></ProtectedRoute>} />
            <Route path="/super-admin/all-users" element={<ProtectedRoute allowedRoles={['superadmin']}><AllUsers /></ProtectedRoute>} />
            <Route path="/audit-logging-system" element={<ProtectedRoute allowedRoles={['superadmin', 'corporate']}><AuditLoggingSystem /></ProtectedRoute>} />
            <Route path="/performance-health-monitor" element={<ProtectedRoute allowedRoles={['superadmin']}><PerformanceHealthMonitor /></ProtectedRoute>} />

            {/* Corporate Management Routes - Protected */}
            <Route path="/job-management" element={<ProtectedRoute allowedRoles={['corporate']}><JobManagement /></ProtectedRoute>} />
            <Route path="/corporate/reports" element={<ProtectedRoute allowedRoles={['corporate']}><CorporateReports /></ProtectedRoute>} />
            <Route path="/corporate/earnings" element={<ProtectedRoute allowedRoles={['corporate']}><CorporateEarnings /></ProtectedRoute>} />
            <Route path="/corporate/clients-agencies" element={<ProtectedRoute allowedRoles={['corporate']}><ClientsAgencies /></ProtectedRoute>} />

            {/* Quotations Routes - Protected */}
            <Route path="/corporate/quotes" element={<ProtectedRoute allowedRoles={['corporate']}><CorporateQuotes /></ProtectedRoute>} />
            <Route path="/corporate/quotes/new" element={<ProtectedRoute allowedRoles={['corporate']}><QuoteEditor /></ProtectedRoute>} />
            <Route path="/corporate/quotes/:id" element={<ProtectedRoute allowedRoles={['corporate']}><ViewQuote /></ProtectedRoute>} />
            <Route path="/corporate/quotes/:id/edit" element={<ProtectedRoute allowedRoles={['corporate']}><QuoteEditor /></ProtectedRoute>} />

            {/* Inspection Templates Routes - Protected */}
            <Route path="/corporate/templates" element={<ProtectedRoute allowedRoles={['corporate']}><CorporateTemplates /></ProtectedRoute>} />
            <Route path="/corporate/templates/new" element={<ProtectedRoute allowedRoles={['corporate']}><TemplateBuilder /></ProtectedRoute>} />
            <Route path="/corporate/templates/:id" element={<ProtectedRoute allowedRoles={['corporate']}><TemplateBuilder /></ProtectedRoute>} />

            {/* Inspections Routes - Protected */}
            <Route path="/corporate/inspections" element={<ProtectedRoute allowedRoles={['corporate']}><CorporateInspections /></ProtectedRoute>} />
            <Route path="/contractor/inspections" element={<ProtectedRoute allowedRoles={['contractor']}><ContractorInspections /></ProtectedRoute>} />

            {/* Inspection Conduct/View Routes - Protected (accessible by both corporate and contractor) */}
            <Route path="/inspection/:id/conduct" element={<ProtectedRoute allowedRoles={['corporate', 'contractor']}><ConductInspection /></ProtectedRoute>} />
            <Route path="/inspection/:id/review" element={<ProtectedRoute allowedRoles={['corporate', 'contractor']}><ReviewInspection /></ProtectedRoute>} />
            <Route path="/inspection/:id/pdf" element={<ProtectedRoute allowedRoles={['corporate', 'contractor']}><InspectionPdf /></ProtectedRoute>} />
            <Route path="/inspection/:id/report" element={<ProtectedRoute allowedRoles={['corporate', 'contractor']}><InspectionReport /></ProtectedRoute>} />
            <Route path="/inspection/:id" element={<ProtectedRoute allowedRoles={['corporate', 'contractor']}><ViewInspection /></ProtectedRoute>} />
            <Route path="/contractor-job-execution/:instanceId" element={<ProtectedRoute allowedRoles={['contractor']}><ContractorJobExecution /></ProtectedRoute>} />
            <Route path="/workforce-management" element={<ProtectedRoute allowedRoles={['corporate']}><WorkforceManagement /></ProtectedRoute>} />

            {/* Multi-Company Routes - Protected */}
            <Route path="/multi-company-switcher" element={<ProtectedRoute><MultiCompanySwitcher /></ProtectedRoute>} />
            <Route path="/company-merge-simulation" element={<ProtectedRoute allowedRoles={['superadmin']}><CompanyMergeSimulation /></ProtectedRoute>} />

            {/* Super Admin Tools - Protected */}
            <Route path="/global-loading-states" element={<ProtectedRoute allowedRoles={['superadmin']}><GlobalLoadingStates /></ProtectedRoute>} />
            <Route path="/error-boundary-system" element={<ProtectedRoute allowedRoles={['superadmin']}><ErrorBoundarySystem /></ProtectedRoute>} />

            {/* 404 Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;